/**
 * Draws placed subtitles onto a 2D canvas: the page's preview canvas and the
 * worker's OffscreenCanvas run this same code, so the preview is the output.
 *
 * Per cue: an opaque box behind each line (BorderStyle 3), else the shadow,
 * then every run's outline, then every run's fill — outlines first across the
 * whole cue, so one run's outline never covers its neighbour's letters.
 */

import { activeCues, indexCues, type RenderTrack, type Rgba } from './model.ts';
import {
  type Frame,
  layoutFrame,
  type MeasureText,
  type PlacedCue,
  type PlacedRun,
} from './layout.ts';

export type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export function rgbaCss(c: Rgba, alpha = 1): string {
  const a = Math.max(0, Math.min(1, c.a * alpha));
  return `rgba(${c.r},${c.g},${c.b},${Math.round(a * 1000) / 1000})`;
}

/** measureText behind a cache: a cue's words are measured once, not once per frame. */
export function cachedMeasure(ctx: Ctx2D): MeasureText {
  const cache = new Map<string, number>();
  return (text, font) => {
    const key = `${font}\u0000${text}`;
    const hit = cache.get(key);
    if (hit !== undefined) return hit;
    if (cache.size > 50_000) cache.clear();
    ctx.font = font;
    const width = ctx.measureText(text).width;
    cache.set(key, width);
    return width;
  };
}

function decorate(ctx: Ctx2D, run: PlacedRun, baseline: number, color: string): void {
  const thickness = Math.max(1, run.size / 16);
  ctx.fillStyle = color;
  if (run.style.underline) ctx.fillRect(run.x, baseline + run.size * 0.1, run.width, thickness);
  if (run.style.strikeOut) ctx.fillRect(run.x, baseline - run.size * 0.3, run.width, thickness);
}

function drawRunText(
  ctx: Ctx2D,
  run: PlacedRun,
  baseline: number,
  dx: number,
  dy: number,
  mode: 'stroke' | 'fill',
): void {
  ctx.font = run.font;
  if (mode === 'stroke') ctx.strokeText(run.text, run.x + dx, baseline + dy);
  else ctx.fillText(run.text, run.x + dx, baseline + dy);
}

export function drawPlaced(ctx: Ctx2D, placed: readonly PlacedCue[]): void {
  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.lineJoin = 'round';
  ctx.miterLimit = 2;
  for (const cue of placed) {
    if (cue.alpha <= 0) continue;
    if (cue.box) {
      for (const line of cue.lines) {
        const pad = line.runs.reduce((m, r) => Math.max(m, r.outline), 0);
        ctx.fillStyle = rgbaCss(cue.box, cue.alpha);
        ctx.fillRect(line.x - pad, line.y - pad / 2, line.width + pad * 2, line.height + pad);
      }
    } else {
      // Shadow: the outlined text again, offset down and right, in the shadow colour.
      for (const line of cue.lines) {
        for (const run of line.runs) {
          if (run.shadow <= 0 || run.style.shadow.a <= 0) continue;
          const color = rgbaCss(run.style.shadow, cue.alpha);
          if (run.outline > 0) {
            ctx.strokeStyle = color;
            ctx.lineWidth = run.outline * 2;
            drawRunText(ctx, run, line.baseline, run.shadow, run.shadow, 'stroke');
          }
          ctx.fillStyle = color;
          drawRunText(ctx, run, line.baseline, run.shadow, run.shadow, 'fill');
        }
      }
      for (const line of cue.lines) {
        for (const run of line.runs) {
          if (run.outline <= 0 || run.style.outline.a <= 0) continue;
          ctx.strokeStyle = rgbaCss(run.style.outline, cue.alpha);
          ctx.lineWidth = run.outline * 2;
          drawRunText(ctx, run, line.baseline, 0, 0, 'stroke');
        }
      }
    }
    for (const line of cue.lines) {
      for (const run of line.runs) {
        const fill = rgbaCss(run.style.fill, cue.alpha);
        ctx.fillStyle = fill;
        drawRunText(ctx, run, line.baseline, 0, 0, 'fill');
        if (run.style.underline || run.style.strikeOut) decorate(ctx, run, line.baseline, fill);
      }
    }
  }
  ctx.restore();
}

export interface SubtitleRenderer {
  /** Draws whatever shows at `t` seconds onto a frame already painted on `ctx`. */
  draw(ctx: Ctx2D, frame: Frame, t: number): void;
}

export function createRenderer(track: RenderTrack): SubtitleRenderer {
  const index = indexCues(track.cues);
  let measure: MeasureText | null = null;
  let measuredOn: Ctx2D | null = null;
  return {
    draw(ctx, frame, t) {
      const cues = activeCues(index, t);
      if (cues.length === 0) return;
      if (measuredOn !== ctx || !measure) {
        measure = cachedMeasure(ctx);
        measuredOn = ctx;
      }
      drawPlaced(ctx, layoutFrame(cues, track, frame, measure, t));
    },
  };
}
